import { useState, useEffect, useCallback } from 'react';
import {
  Building2,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  X,
  Loader2,
  Edit2,
  Trash2,
  Network,
  List,
  AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { EntityType, ConsolidationMethod, type Entity, type Currency } from '@/types';
import { entitiesApi, currenciesApi } from '@/api/client';
import SearchableSelect from '@/components/SearchableSelect';
import { useAppStore } from '@/store';

// --- Entity Type Badge -------------------------------------------------------

// Excel-friendly consolidation method labels (backend: full, proportional, equity, none)
const CONSOLIDATION_METHOD_LABELS: Record<string, string> = {
  full: 'Subsidiary',
  proportional: 'Aggregate',
  equity: 'Equity accounting',
  none: 'Inactive',
};

function EntityTypeBadge({ type }: { type: EntityType }) {
  const config: Record<EntityType, { label: string; bg: string; text: string }> = {
    [EntityType.PARENT]: { label: 'Parent', bg: 'bg-blue-50', text: 'text-blue-700' },
    [EntityType.SUBSIDIARY]: { label: 'Subsidiary', bg: 'bg-green-50', text: 'text-green-700' },
    [EntityType.ASSOCIATE]: { label: 'Associate', bg: 'bg-amber-50', text: 'text-amber-700' },
    [EntityType.JOINT_VENTURE]: { label: 'Joint Venture', bg: 'bg-purple-50', text: 'text-purple-700' },
  };
  const c = config[type];
  return <span className={clsx('badge', c.bg, c.text)}>{c.label}</span>;
}

// --- Entity Form Modal -------------------------------------------------------

const DEFAULT_CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'JPY', 'SGD', 'SEK', 'CHF', 'AUD', 'CAD', 'CNY', 'INR', 'KES'];

interface EntityFormProps {
  entity?: Entity | null;
  entities: Entity[];
  currencies: Currency[];
  onSave: (data: Partial<Entity>) => Promise<void>;
  onClose: () => void;
}

function EntityFormModal({ entity, entities, currencies, onSave, onClose }: EntityFormProps) {
  const [formData, setFormData] = useState<Partial<Entity>>({
    code: entity?.code || '',
    name: entity?.name || '',
    entity_type: entity?.entity_type || EntityType.SUBSIDIARY,
    parent_id: entity?.parent_id || '',
    ownership_pct: entity?.ownership_pct || 100,
    consolidation_method: entity?.consolidation_method || ConsolidationMethod.FULL,
    has_control: entity?.has_control ?? true,
    enable_intercompany: entity?.enable_intercompany ?? false,
    functional_currency: entity?.functional_currency || 'AED',
    country: entity?.country || 'AE',
    is_active: entity?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(formData);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to save entity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {entity ? 'Edit Entity' : 'Add New Entity'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-lg bg-red-50 border border-red-100">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Entity Code</label>
              <input
                className="input-field"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="e.g., ACME-EU"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
              <select
                className="select-field"
                value={formData.functional_currency}
                onChange={(e) => setFormData({ ...formData, functional_currency: e.target.value })}
              >
                {(currencies.length > 0
                  ? currencies.filter((c) => c.is_active).map((c) => c.code)
                  : DEFAULT_CURRENCIES
                ).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Entity Name</label>
            <input
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Full legal entity name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Entity Type</label>
              <select
                className="select-field"
                value={formData.entity_type}
                onChange={(e) => setFormData({ ...formData, entity_type: e.target.value as EntityType })}
              >
                <option value={EntityType.PARENT}>Parent</option>
                <option value={EntityType.SUBSIDIARY}>Subsidiary</option>
                <option value={EntityType.ASSOCIATE}>Associate</option>
                <option value={EntityType.JOINT_VENTURE}>Joint Venture</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Consolidation Method</label>
              <select
                className="select-field"
                value={formData.consolidation_method}
                onChange={(e) => setFormData({ ...formData, consolidation_method: e.target.value as ConsolidationMethod })}
              >
                <option value={ConsolidationMethod.FULL}>
                  {formData.entity_type === EntityType.PARENT ? 'Holding' : 'Subsidiary'}
                </option>
                <option value={ConsolidationMethod.PROPORTIONAL}>Aggregate</option>
                <option value={ConsolidationMethod.EQUITY}>Equity accounting</option>
                <option value={ConsolidationMethod.NOT_CONSOLIDATED}>Inactive</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Parent Entity</label>
              <SearchableSelect
                options={[
                  { value: '', label: 'None (Top-level)' },
                  ...entities
                    .filter((ent) => ent.id !== entity?.id)
                    .map((ent) => ({ value: String(ent.id), label: ent.name, secondary: ent.code })),
                ]}
                value={formData.parent_id || ''}
                onChange={(v) => setFormData({ ...formData, parent_id: v || null })}
                placeholder="Select parent..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Ownership %</label>
              <input
                type="number"
                className="input-field"
                value={formData.ownership_pct}
                onChange={(e) => setFormData({ ...formData, ownership_pct: parseFloat(e.target.value) || 0 })}
                min={0}
                max={100}
                step={0.01}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Country (ISO 3166)</label>
              <input
                className="input-field"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value.toUpperCase().slice(0, 3) })}
                placeholder="e.g., AE"
                maxLength={3}
              />
            </div>
            <div className="flex items-end pb-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Active entity</label>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6 flex-wrap pt-2 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_control"
                checked={formData.has_control ?? true}
                onChange={(e) => setFormData({ ...formData, has_control: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="has_control" className="text-sm text-gray-700">Has control</label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enable_intercompany"
                checked={formData.enable_intercompany ?? false}
                onChange={(e) => setFormData({ ...formData, enable_intercompany: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="enable_intercompany" className="text-sm text-gray-700">Enable intercompany</label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !formData.code || !formData.name} className="btn-primary">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : entity ? 'Update Entity' : 'Create Entity'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Delete Confirmation Modal -----------------------------------------------

interface DeleteConfirmProps {
  entity: Entity;
  childCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  deleting: boolean;
}

function DeleteConfirmModal({ entity, childCount, onConfirm, onCancel, deleting }: DeleteConfirmProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Trash2 className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Entity</h3>
              <p className="text-sm text-gray-500">This action cannot be undone</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm font-medium text-gray-800">{entity.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">Code: {entity.code} &middot; {entity.entity_type} &middot; {entity.ownership_pct}%</p>
          </div>

          {childCount > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100 mb-4">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                This entity has <strong>{childCount} child {childCount === 1 ? 'entity' : 'entities'}</strong>.
                Deleting it will remove the parent reference from all children.
              </p>
            </div>
          )}

          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>&quot;{entity.name}&quot;</strong>?
            The entity will be deactivated and removed from the hierarchy.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onCancel} disabled={deleting} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Entity
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Tree View ---------------------------------------------------------------

function EntityTreeNode({
  entity,
  level = 0,
  children: entityChildren,
  onEdit,
  onDelete,
}: {
  entity: Entity;
  level?: number;
  children: Entity[];
  onEdit: (entity: Entity) => void;
  onDelete: (entity: Entity) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const directChildren = entityChildren.filter((e) => e.parent_id === entity.id);
  const hasChildren = directChildren.length > 0;

  return (
    <div>
      <div
        className={clsx(
          'group flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors',
        )}
        style={{ paddingLeft: `${level * 24 + 12}px` }}
        onClick={() => setExpanded(!expanded)}
      >
        {hasChildren ? (
          expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />
        ) : (
          <div className="w-4" />
        )}
        <div
          className={clsx(
            'w-2 h-2 rounded-full flex-shrink-0',
            entity.entity_type === EntityType.PARENT && 'bg-blue-500',
            entity.entity_type === EntityType.SUBSIDIARY && 'bg-green-500',
            entity.entity_type === EntityType.ASSOCIATE && 'bg-amber-500',
            entity.entity_type === EntityType.JOINT_VENTURE && 'bg-purple-500',
          )}
        />
        <span className={clsx('text-sm font-medium', entity.is_active ? 'text-gray-800' : 'text-gray-400 line-through')}>{entity.name}</span>
        <span className="text-xs text-gray-400 ml-1">({entity.code})</span>
        <EntityTypeBadge type={entity.entity_type} />
        {!entity.is_active && <span className="badge bg-gray-100 text-gray-500">Inactive</span>}
        <span className="text-xs text-gray-500 ml-auto mr-2">{entity.ownership_pct}% &middot; {entity.functional_currency}</span>
        <div className="hidden group-hover:flex items-center gap-0.5">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(entity); }}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Edit entity"
          >
            <Edit2 className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(entity); }}
            className="p-1 hover:bg-red-50 rounded transition-colors"
            title="Delete entity"
          >
            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>
      {expanded && hasChildren && (
        <div>
          {directChildren.map((child) => (
            <EntityTreeNode key={child.id} entity={child} level={level + 1} children={entityChildren} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- Entities Page ------------------------------------------------------------

export default function Entities() {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editEntity, setEditEntity] = useState<Entity | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('tree');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const { addNotification } = useAppStore();

  const loadEntities = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [response, currenciesRes] = await Promise.all([
        entitiesApi.list({ page: 1, page_size: 5000, is_active: showInactive ? undefined : true }),
        currenciesApi.list().catch(() => [] as Currency[]),
      ]);
      setEntities(response.items);
      setCurrencies(currenciesRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load entities');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    loadEntities();
  }, [loadEntities]);

  const filteredEntities = entities.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.code.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const rootEntities = entities.filter((e) => !e.parent_id);

  const handleSave = async (data: Partial<Entity>) => {
    if (editEntity) {
      await entitiesApi.update(editEntity.id, data);
      addNotification({ type: 'success', title: 'Entity Updated', message: `${data.name || editEntity.name} has been updated.` });
    } else {
      await entitiesApi.create(data);
      addNotification({ type: 'success', title: 'Entity Created', message: `${data.name} has been created.` });
    }
    setShowModal(false);
    setEditEntity(null);
    await loadEntities();
  };

  const [deleteTarget, setDeleteTarget] = useState<Entity | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await entitiesApi.delete(deleteTarget.id);
      addNotification({ type: 'success', title: 'Entity Deleted', message: `${deleteTarget.name} has been deleted.` });
      setDeleteTarget(null);
      await loadEntities();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete entity.' });
    } finally {
      setDeleting(false);
    }
  };

  const getChildCount = (entityId: string) => entities.filter((e) => e.parent_id === entityId).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Entities</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your group structure and legal entities</p>
        </div>
        <button
          onClick={() => { setEditEntity(null); setShowModal(true); }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Entity
        </button>
      </div>

      {/* Search & View Toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            className="input-field pl-9"
            placeholder="Search entities by name or code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            role="switch"
            aria-checked={showInactive}
            tabIndex={0}
            onClick={() => setShowInactive((v) => !v)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowInactive((v) => !v); } }}
            className={clsx(
              'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
              showInactive ? 'bg-blue-600' : 'bg-gray-300',
            )}
          >
            <span
              className={clsx(
                'inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform',
                showInactive ? 'translate-x-4.5' : 'translate-x-0.5',
              )}
            />
          </div>
          <span className="text-xs font-medium text-gray-600">Show Inactive</span>
        </label>
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('table')}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <List className="w-3.5 h-3.5" />
            Table
          </button>
          <button
            onClick={() => setViewMode('tree')}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
              viewMode === 'tree' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700',
            )}
          >
            <Network className="w-3.5 h-3.5" />
            Hierarchy
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Failed to load entities</h3>
          <p className="text-sm text-red-600 mt-1">{error}</p>
          <button onClick={loadEntities} className="btn-primary mt-4">Retry</button>
        </div>
      )}

      {/* Content */}
      {!error && isLoading ? (
        <div className="card">
          <div className="p-6 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="skeleton w-20 h-4" />
                <div className="skeleton w-48 h-4" />
                <div className="skeleton w-16 h-5 rounded-full" />
                <div className="skeleton w-32 h-4" />
                <div className="skeleton w-12 h-4" />
                <div className="skeleton w-12 h-4" />
              </div>
            ))}
          </div>
        </div>
      ) : !error && viewMode === 'tree' ? (
        <div className="card p-4">
          {rootEntities.length === 0 ? (
            <div className="py-12 text-center">
              <Building2 className="w-10 h-10 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">No entities found</p>
            </div>
          ) : (
            rootEntities.map((entity) => (
              <EntityTreeNode
                key={entity.id}
                entity={entity}
                children={entities}
                onEdit={(ent) => { setEditEntity(ent); setShowModal(true); }}
                onDelete={(ent) => setDeleteTarget(ent)}
              />
            ))
          )}
        </div>
      ) : !error ? (
        <div className="card overflow-hidden">
          {filteredEntities.length === 0 ? (
            <div className="py-16 text-center">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-sm font-medium text-gray-900">No entities found</h3>
              <p className="text-sm text-gray-500 mt-1">
                {searchQuery ? 'Try adjusting your search query' : 'Get started by adding your first entity'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="table-header">Code</th>
                    <th className="table-header">Name</th>
                    <th className="table-header">Type</th>
                    <th className="table-header">Parent</th>
                    <th className="table-header text-right">Ownership %</th>
                    <th className="table-header">Currency</th>
                    <th className="table-header">Status</th>
                    <th className="table-header w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredEntities.map((entity) => (
                    <>
                      <tr
                        key={entity.id}
                        className="hover:bg-gray-50/50 cursor-pointer transition-colors"
                        onClick={() => setExpandedRow(expandedRow === entity.id ? null : entity.id)}
                      >
                        <td className="table-cell font-mono text-xs font-medium text-gray-600">
                          {entity.code}
                        </td>
                        <td className="table-cell font-medium text-gray-900">{entity.name}</td>
                        <td className="table-cell">
                          <EntityTypeBadge type={entity.entity_type} />
                        </td>
                        <td className="table-cell text-gray-500">
                          {entity.parent_name || <span className="text-gray-300">&mdash;</span>}
                        </td>
                        <td className="table-cell text-right font-mono text-sm">
                          {Number(entity.ownership_pct).toFixed(1)}%
                        </td>
                        <td className="table-cell">
                          <span className="badge bg-gray-100 text-gray-700">{entity.functional_currency}</span>
                        </td>
                        <td className="table-cell">
                          {entity.is_active ? (
                            <span className="badge bg-green-50 text-green-700">Active</span>
                          ) : (
                            <span className="badge bg-gray-100 text-gray-500">Inactive</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditEntity(entity); setShowModal(true); }}
                              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit entity"
                            >
                              <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(entity); }}
                              className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete entity"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expandedRow === entity.id && (
                        <tr key={`${entity.id}-details`}>
                          <td colSpan={8} className="px-4 py-4 bg-gray-50/50">
                            <div className="grid grid-cols-4 gap-6 text-sm">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Consolidation Method</p>
                                <p className="text-gray-800">
                  {entity.entity_type === EntityType.PARENT && entity.consolidation_method === ConsolidationMethod.FULL
                    ? 'Holding'
                    : (CONSOLIDATION_METHOD_LABELS[entity.consolidation_method] ?? entity.consolidation_method)}
                </p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Functional Currency</p>
                                <p className="text-gray-800">{entity.functional_currency}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Created</p>
                                <p className="text-gray-800">{new Date(entity.created_at).toLocaleDateString()}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Entity ID</p>
                                <p className="text-gray-500 font-mono text-xs">{entity.id}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Summary bar */}
      {!isLoading && !error && entities.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {entities.length} total entities
          </span>
          <span>{entities.filter((e) => e.entity_type === EntityType.SUBSIDIARY).length} subsidiaries</span>
          <span>{entities.filter((e) => e.entity_type === EntityType.ASSOCIATE).length} associates</span>
          <span>{entities.filter((e) => e.entity_type === EntityType.JOINT_VENTURE).length} joint ventures</span>
        </div>
      )}

      {/* Form Modal */}
      {showModal && (
        <EntityFormModal
          entity={editEntity}
          entities={entities}
          currencies={currencies}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditEntity(null); }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          entity={deleteTarget}
          childCount={getChildCount(deleteTarget.id)}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
